import React, { useEffect, useCallback } from 'react';
import { Row, Col, FormGroup } from 'react-bootstrap';
import { cloneDeep, set, get } from 'lodash';
import Select from 'react-select';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { customStyles, inputTypes, manualLabelColours, manualStatusColours, checkIsNumber } from '../../../../Components/helper';
import { useCustomColumnState } from '../CustomColumnContextProvider';
import { customColumnPath, manualPath } from '../fieldPath';
import RenderOptionsType from './RenderOptionsType';
import RenderLabelsOPtions from './RenderLabelsTypeOptions';

function ManualComponent({ data, pIndex }) {
  const {
    customColumnState: { isEditMode },
    customColumnState,
    setCustomColumnState,
    globalError,
  } = useCustomColumnState();
  const { t } = useTranslation('common');

  useEffect(() => {
    if (manualLabelColours && !isEditMode) {
      const copyState = customColumnState;
      set(copyState, customColumnPath(pIndex, 'labelColoursOptions').COMMONPATH, manualLabelColours);
      setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
    }
  }, [manualLabelColours, isEditMode]);

  useEffect(() => {
    if (manualStatusColours && !isEditMode) {
      const copyState = customColumnState;
      set(copyState, customColumnPath(pIndex, 'statusColoursOptions').COMMONPATH, manualStatusColours);
      setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
    }
  }, [manualStatusColours, isEditMode]);

  useEffect(() => {
    if (data?.statusColoursOptions && !isEditMode) {
      const copyState = customColumnState;
      set(copyState, customColumnPath(pIndex, 'status').COMMONPATH, data?.statusColoursOptions[0]);
      setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
    }
  }, [data?.statusColoursOptions, isEditMode]);

  const handleChange = (value, path) => {
    const copyState = customColumnState;
    set(copyState, path, value);
    if (['Checkbox', 'Radio_button', 'Single_select_Dropdown', 'Multi_select_Dropdown'].includes(value?.name)) {
      set(copyState, customColumnPath(pIndex, 'options').COMMONPATH, [{ name: '', position: 0 }]);
    }
    //  else {
    //   set(copyState, customColumnPath(pIndex, "options").COMMONPATH, [,]);
    // }
    setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
  };

  const addNew = () => {
    const copyState = customColumnState;
    const manualOptions = cloneDeep(get(copyState, manualPath(pIndex).MANUAL_OPTIONS, []));
    manualOptions.push({ name: '' });
    set(copyState, manualPath(pIndex).MANUAL_OPTIONS, manualOptions);
    setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
  };

  const remove = (inde) => {
    const copyState = customColumnState;
    const manualOptions = cloneDeep(get(copyState, manualPath(pIndex).MANUAL_OPTIONS, []));
    manualOptions.splice(inde, 1);
    set(copyState, manualPath(pIndex).MANUAL_OPTIONS, manualOptions);
    setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
  };
  const addNewLabel = () => {
    const copyState = customColumnState;
    const labelArr = cloneDeep(get(copyState, customColumnPath(pIndex, 'labelColoursOptions').COMMONPATH, []));
    labelArr.push({
      colourName: '',
      colourHexCode: '',
      isNewAdded: true,
    });
    set(copyState, customColumnPath(pIndex, 'labelColoursOptions').COMMONPATH, labelArr);
    setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
  };

  const removeLabel = (inde) => {
    const copyState = customColumnState;
    const labelArr = cloneDeep(get(copyState, customColumnPath(pIndex, 'labelColoursOptions').COMMONPATH, []));
    labelArr.splice(inde, 1);
    set(copyState, customColumnPath(pIndex, 'labelColoursOptions').COMMONPATH, labelArr);
    setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
  };

  const renderOptions = useCallback(
    (item, index) => {
      return (
        <RenderOptionsType
          key={item?._id}
          id={item?.id}
          column={item}
          index={index}
          moveTr={moveTr}
          addNew={addNew}
          remove={remove}
          pIndex={pIndex}
          wholeArry={data?.options}
          handleChange={handleChange}
        />
      );
    },
    [data?.options],
  );

  const moveTr = (dragIndex, hoverIndex) => {
    const currList = swapArrayLocs(data?.options, dragIndex, hoverIndex);
    const copyState = customColumnState;
    set(copyState, customColumnPath(pIndex, 'options').COMMONPATH, currList);
    setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
  };
  function swapArrayLocs(arr, index1, index2) {
    const temp = arr[index1];
    arr[index1] = arr[index2];
    arr[index2] = temp;
    return arr;
  }

  const renderLabels = useCallback(
    (item, index) => {
      return (
        <RenderLabelsOPtions
          key={item?._id}
          id={item?.id}
          column={item}
          index={index}
          moveTr={moveLabelOption}
          addNewLabel={addNewLabel}
          removeLabel={removeLabel}
          pIndex={pIndex}
          wholeArry={data?.labelColoursOptions}
          handleChange={handleChange}
        />
      );
    },
    [data?.labelColoursOptions],
  );

  const moveLabelOption = (dragIndex, hoverIndex) => {
    const currList = swapArrayLocs(data?.labelColoursOptions, dragIndex, hoverIndex);
    const copyState = customColumnState;
    set(copyState, customColumnPath(pIndex, 'labelColoursOptions').COMMONPATH, currList);
    setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Row>
        <Col lg="12">
          <FormGroup className="mb-4">
            <Select
              name="type"
              options={inputTypes || []}
              getOptionLabel={(option) => `${option.type}`}
              getOptionValue={(option) => `${option.type}`}
              value={data?.type}
              className="common-select"
              classNamePrefix="common-select"
              styles={customStyles}
              placeholder={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.MANUAL_COMPONENET.SELECT_TYPE')}
              maxMenuHeight="110px"
              isDisabled={isEditMode}
              onChange={(selected) => handleChange(selected, manualPath(pIndex, 'type').MANUAL_PATH)}
            />
            <span className="error-message position-absolute">{get(globalError, `manual[${pIndex}].type`, '')}</span>
          </FormGroup>
        </Col>
        {['Checkbox', 'Radio_button', 'Single_select_Dropdown', 'Multi_select_Dropdown'].includes(data?.type?.name) && (
          <Col lg="12">
            <div>
              <label>{t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.MANUAL_COMPONENET.OPTIONS')}</label>
              {data?.options?.map((op, opIn) => {
                return renderOptions(op, opIn);
              })}
            </div>
          </Col>
        )}

        {['Short_Text'].includes(data?.type?.name) && (
          <Col lg="12">
            <FormGroup className="mb-2">
              <div className="input-group form-floating-label fixed-label">
                <input
                  type="text"
                  className="form-control f-14"
                  value={data?.characterLimit}
                  placeholder={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.MANUAL_COMPONENET.ADD_CHARACTER_LIMIT')}
                  // disabled={isEditMode}
                  onChange={(e) => {
                    if (checkIsNumber(e.target.value)) {
                      handleChange(e.target.value, customColumnPath(pIndex, 'characterLimit').COMMONPATH);
                    }
                  }}
                />
                <label className="asterisk-req">{t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.MANUAL_COMPONENET.CHARACTER_LIMIT')}</label>
              </div>
              <span className="error-message position-absolute">{get(globalError, `manual[${pIndex}].characterLimit`, '')}</span>
            </FormGroup>
          </Col>
        )}

        {['Label'].includes(data?.type?.name) && (
          <Col lg="12">
            <FormGroup className="mb-2">
              <div className="input-group form-floating-label fixed-label">
                {data?.labelColoursOptions?.map((col, ind) => {
                  return renderLabels(col, ind);
                })}
              </div>
            </FormGroup>
          </Col>
        )}

        {['Status'].includes(data?.type?.name) && (
          <Col lg={{ span: 6, offset: 3 }}>
            <FormGroup className="mb-2">
              <div className="input-group form-floating-label fixed-label">
                <button
                  className="btn btn-default w-100 m-0 mb-2"
                  onClick={() => handleChange(!data?.isStatusEditTrue, customColumnPath(pIndex, 'isStatusEditTrue').COMMONPATH)}
                >
                  <span className="icon-edit mr-2" />
                  {t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.MANUAL_COMPONENET.EDIT_COLOUR')}
                </button>
                {data?.statusColoursOptions?.map((stat, keyIn) => {
                  return (
                    <>
                      <div
                        className="status-color-option"
                        onClick={(e) => handleChange(stat, customColumnPath(pIndex, 'status').COMMONPATH)}
                        style={data?.status?.colourHexCode === stat?.colourHexCode ? { border: '2px solid #8d8dff' } : {}}
                      >
                        <div className="color-content">
                          <input
                            type="color"
                            className="form-control"
                            value={stat?.colourHexCode}
                            onChange={(e) =>
                              handleChange(e.target.value, customColumnPath(pIndex, 'statusColoursOptions', keyIn, 'colourHexCode').STATUS_COLOURS)
                            }
                            disabled={!data?.isStatusEditTrue}
                          />
                        </div>
                        <input
                          type="text"
                          className="text-content f-14"
                          value={stat?.colourName}
                          placeholder={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.MANUAL_COMPONENET.ADD_COLOUR_NAME')}
                          onChange={(e) => handleChange(e.target.value, customColumnPath(pIndex, 'statusColoursOptions', keyIn, 'colourName').STATUS_COLOURS)}
                          readOnly={!data?.isStatusEditTrue}
                        />
                      </div>
                      <br />
                    </>
                  );
                })}
              </div>
            </FormGroup>
          </Col>
        )}
      </Row>
    </DndProvider>
  );
}

export default ManualComponent;
